import { Tooltip } from 'antd';
import katex from 'katex';
import {
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createEditor } from 'slate';
import { RenderElementProps, Slate, withReact } from 'slate-react';
import styled, { css } from 'styled-components';

import theme from '../../../../../assets/theme';
import { newRelicNoticeError } from '../../../../../hooks/useNewRelic';
import { parseMarkdownToSlateV2 } from '../../../../../utils/richTextUtils';
import {
  Attributes,
  CuripodDecendant,
  CuripodElement,
  SlateEditable,
  withMathElements,
} from '../../../../SlideComponents/slateUtil';

const isSlate = (text: string) => {
  return text.startsWith('[{') && text.endsWith('}]');
};

export const MarkdownSlateViewer = ({
  content,
  fontSize,
}: {
  content: string;
  fontSize?: number;
}) => {
  const editor = useMemo(() => withMathElements(withReact(createEditor())), []);
  const [parsedContent, setAsyncParsedContent] = useState<CuripodDecendant[]>([]);

  useEffect(() => {
    const asyncFn = async () => {
      if (isSlate(content)) {
        setAsyncParsedContent(JSON.parse(content) as CuripodDecendant[]);
      } else {
        const parsed = await parseMarkdownToSlateV2(content);
        setAsyncParsedContent(parsed as CuripodDecendant[]);
      }
    };
    asyncFn();
  }, [content]);

  const renderElement = useCallback(
    //@ts-ignore
    (props: RenderElementProps) => <MarkdownElement {...props} fontSize={fontSize} />,
    [],
  );
  const renderLeaf = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (props: any) => {
      return <Leaf {...props} fontSize={fontSize} />;
    },
    [],
  );

  useEffect(() => {
    editor.children = parsedContent;
    editor.onChange();
  }, [parsedContent, editor]);

  return (
    <AgentSlateWrapper>
      <span translate="no">
        <Slate editor={editor} value={parsedContent}>
          <SlateEditable
            readOnly={true}
            $readOnly={true}
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            style={{
              backgroundColor: 'transparent',
              padding: 0,
              margin: 0,
              border: 'none',
              boxShadow: 'none',
              fontSize: fontSize,
            }}
          />
        </Slate>
      </span>
    </AgentSlateWrapper>
  );
};

// This wrappers adds a bit of spacing between the top level blocks and nested blocks
const AgentSlateWrapper = styled.div`
  position: relative;
  width: 100%;
  ul,
  ol {
    padding-inline-start: 10px;
  }

  ul,
  p,
  ol {
    margin-bottom: 4px; // Default 4px margin on all nested blocks
  }

  div > p {
    margin-bottom: 12px; // Default 12px margin on top level blocks
  }
  div > ul {
    margin-bottom: 12px; // Default 12px margin on top level blocks
  }
  div > ol {
    margin-bottom: 12px; // Default 12px margin on top level blocks
  }
`;

interface LatexRendererProps {
  value: string;
  display?: boolean; // true = block mode
  color?: string; // color for the math expression
}

export const LatexRenderer: React.FC<LatexRendererProps> = ({
  value,
  display = false,
  color,
}) => {
  try {
    const html = katex.renderToString(value, {
      throwOnError: false,
      displayMode: display,
      output: 'html',
    });

    return (
      <span
        style={{
          fontFamily: 'KaTeX_Main, Times New Roman, serif',
          overflow: 'unset',
          color: color || 'inherit',
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  } catch (err) {
    // Track the error in New Relic for monitoring
    newRelicNoticeError(err, 'KaTeX LaTeX parsing error', {
      latexValue: value,
      displayMode: display,
      component: 'LatexRenderer',
      context: 'MarkdownSlateViewer',
    });
    return <span>{value}</span>;
  }
};

/* Markdown elements have a margin-bottom  */
export const MarkdownElement = ({
  attributes,
  children,
  element,
  className,
  fontSize,
}: PropsWithChildren<{
  attributes: Attributes;
  element: CuripodElement;
  className?: string;
  fontSize?: number;
}>) => {
  const style = { textAlign: element.align };
  const elementFontSize = fontSize || element.size;
  switch (element.type) {
    case 'bulleted-list':
      return (
        <UnorderedListMarkdown
          style={style}
          {...attributes}
          $fontSize={elementFontSize}
          className={className}
          $align={element.align}
          $color={element.color}
        >
          {children}
        </UnorderedListMarkdown>
      );
    case 'numbered-list':
      return (
        <OrderedListMarkdown
          style={style}
          {...attributes}
          $fontSize={elementFontSize}
          className={className}
          $align={element.align}
          $color={element.color}
        >
          {children}
        </OrderedListMarkdown>
      );
    case 'list-item':
      return (
        <ListItem
          style={style}
          $color={element.color}
          {...attributes}
          $fontSize={elementFontSize}
          $align={element.align}
          className={className}
        >
          {children}
        </ListItem>
      );

    case 'math-inline':
      // Extract color from the text children
      const inlineTextChild = element.children.find(
        child => child && typeof child === 'object' && 'text' in child,
      ) as { color?: string } | undefined;
      const inlineTextColor = inlineTextChild?.color;
      return (
        <span
          {...attributes}
          className={className}
          style={{ ...style, overflow: 'visible !important' }}
        >
          <LatexRenderer value={element.value || ''} color={inlineTextColor} />
        </span>
      );

    case 'math-block':
      // Extract color from the text children
      const blockTextChild = element.children.find(
        child => child && typeof child === 'object' && 'text' in child,
      ) as { color?: string } | undefined;
      const blockTextColor = blockTextChild?.color;
      return (
        <div {...attributes} className={className} style={style}>
          <LatexRenderer
            value={element.value || ''}
            display={true}
            color={blockTextColor}
          />
          {children}
        </div>
      );
    default:
      return (
        <ParagraphElementMarkdown
          {...attributes}
          style={style}
          $color={element.color}
          $fontSize={elementFontSize}
          $align={element.align}
          className={className}
        >
          {children}
        </ParagraphElementMarkdown>
      );
  }
};

export const Leaf = ({
  attributes,
  children,
  leaf,
  fontSize,
}: {
  attributes: Attributes;
  children: ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  leaf: any;
  fontSize?: number;
}) => {
  if (leaf.bold) {
    children = (
      <SlateLeafSpan $color={leaf.color} $fontSize={fontSize}>
        <strong>{children}</strong>
      </SlateLeafSpan>
    );
  }
  if (leaf.underline) {
    children = (
      <SlateLeafSpan $color={leaf.color} $fontSize={fontSize}>
        <u>{children}</u>
      </SlateLeafSpan>
    );
  }
  if (leaf.italic) {
    children = (
      <SlateLeafSpan $color={leaf.color} $fontSize={fontSize}>
        <em style={{ color: leaf.color }}>{children}</em>
      </SlateLeafSpan>
    );
  }
  if (leaf.link) {
    children = (
      <Tooltip mouseEnterDelay={0.5} title={leaf.link}>
        <SlateLeafSpan
          $color={leaf.color !== theme.colors.black ? leaf.color : theme.colors.blueDark}
          style={{
            textDecoration: 'underline',
            filter: 'brightness(0.8)',
          }}
          $fontSize={fontSize}
        >
          <a href={leaf.link} target="_blank" rel="noreferrer">
            {children}
          </a>
        </SlateLeafSpan>
      </Tooltip>
    );
  }
  return (
    <SlateLeafSpan $color={leaf.color} $fontSize={fontSize} {...attributes}>
      {children}
    </SlateLeafSpan>
  );
};

const SlateLeafSpan = styled.span<{ $color?: string; $fontSize?: number }>`
  color: ${({ $color }) =>
    $color}; // we dont set default black to allow for inheritance and backward compatibility with paragraph element color
  ${({ $fontSize }) =>
    $fontSize &&
    css`
      font-size: ${$fontSize}px;
    `}
  span::selection {
    color: ${({ $color }) => ($color ? $color : 'white')} !important;
    background-color: ${theme.colors.orange} !important;
  }
  a {
    color: ${({ $color }) => ($color ? $color : theme.colors.blueDark)};
  }
`;

export const ParagraphElementMarkdown = styled.p<{
  $fontSize?: number;
  $color?: string;
  $align?: string;
}>`
  height: min-content;
  color: ${({ $color }) => ($color ? $color : 'black')};
  white-space: pre-line;
  text-align: ${({ $align }) => ($align ? $align : 'center')};
  ${({ $fontSize }) =>
    $fontSize &&
    css`
      font-size: ${$fontSize}px;
    `}
`;

const UnorderedListMarkdown = styled.ul<{
  $fontSize?: number;
  $color?: string;
  $align?: string;
}>`
  color: ${({ $color }) => ($color ? $color : 'black')};
  ${({ $fontSize }) =>
    $fontSize &&
    css`
      font-size: ${$fontSize}px;
      margin-left: ${$fontSize}px;
    `}

  &::marker {
    ${({ $fontSize }) =>
      $fontSize &&
      css`
        font-size: ${$fontSize}px;
      `}
    color: ${({ $color }) => ($color ? $color : 'black')};
  }
  text-align: ${({ $align }) => ($align ? $align : 'center')};

`;

const OrderedListMarkdown = styled.ol<{
  $fontSize?: number;
  $color?: string;
  $align?: string;
}>`
  color: ${({ $color }) => ($color ? $color : 'black')};
  ${({ $fontSize }) =>
    $fontSize &&
    css`
      font-size: ${$fontSize}px;
      margin-left: ${$fontSize}px;
    `}

  &::marker {
    ${({ $fontSize }) =>
      $fontSize &&
      css`
        font-size: ${$fontSize}px;
      `}
    color: ${({ $color }) => ($color ? $color : 'black')};
  }
  text-align: ${({ $align }) => ($align ? $align : 'center')};
`;

const ListItem = styled.li<{ $fontSize?: number; $color?: string; $align?: string }>`
  color: ${({ $color }) => ($color ? $color : 'black')};
  text-align: ${({ $align }) => ($align ? $align : 'center')};
`;
